import React, { useMemo } from "react";
import { wrapLabel } from "./radarLabel.js";

// The Seller Radar — one polygon per profile, on a fixed 0-10 scale.
//
// Hand-rolled SVG on purpose. The Intranet frontend has no charting dependency
// today and the existing ScoreBar is hand-drawn; an 8-axis radar on a fixed
// scale is about sixty lines of geometry, and adding a chart bundle to get it
// would be the larger change.
//
// Two rules this component exists to enforce, both of which look like nitpicks
// and are not:
//
// 1. A RADAR AXIS ASSERTS "further out is better". That is true of a judged
//    rubric score and false of almost every measured dimension — talk-to-listen
//    at 100% would plot as a perfect spike while describing a rep who let the
//    buyer say nothing. So measures never appear here; they render as band
//    gauges, which have a healthy range rather than a direction. This is the
//    single easiest way to make the whole surface lie, and it will look
//    perfectly reasonable to whoever does it.
//
// 2. AN UNOBSERVED AXIS IS NOT A ZERO. A rep whose polygon collapses on
//    "objection handling" because no objection was raised will read that as a
//    failing grade on something they never had the chance to do. The shape is
//    drawn through the scored axes only; where it skips one, that edge is
//    dashed, the axis shows "—", and it is named underneath. (It used to pull
//    the vertex to the centre with a hollow marker -- which is exactly where
//    a real 0 goes, so the shape said zero while the label said "—".)

const RINGS = [2, 4, 6, 8, 10];
const MAX = 10;
// Horizontal breathing room for the side labels, which extend away from the
// plot rather than into it.
const SIDE_ROOM = 46;

// Axes are ordered so each dimension occupies one contiguous arc, which makes a
// lopsided polygon read immediately as "it's how they sound" or "it's what they
// said" rather than as noise scattered around the circle.
const DIMENSION_ORDER = { tonality: 0, both: 1, technicality: 2, outcome: 3 };
const DIMENSION_COLOR = {
  tonality: "var(--blue-600)",
  technicality: "var(--green-600)",
  both: "var(--c-600)",
  outcome: "var(--c-400)",
};
const DIMENSION_LABEL = {
  tonality: "Voice & delivery",
  technicality: "Substance",
  both: "Both",
  outcome: "Measured later",
};

function polar(cx, cy, radius, index, count) {
  // Start at twelve o'clock and go clockwise, which is how people read a dial.
  const angle = (Math.PI * 2 * index) / count - Math.PI / 2;
  return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
}

export function orderAxes(parameters) {
  return [...parameters].sort((a, b) => {
    const da = DIMENSION_ORDER[a.dimension] ?? 9;
    const db = DIMENSION_ORDER[b.dimension] ?? 9;
    return da - db || a.key.localeCompare(b.key);
  });
}

export default function RadarChart({
  axes,
  overlay = null,
  overlayLabel = "Team median",
  size = 300,
}) {
  const ordered = useMemo(() => orderAxes(axes || []), [axes]);
  const count = ordered.length;

  // Three axes is a triangle, which reads terribly and invites comparisons the
  // shape cannot support. Below that the bars underneath are the honest view.
  if (count < 3) return null;

  const pad = 58;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - pad;

  const pointFor = (value, i) => polar(cx, cy, (Math.max(0, Math.min(MAX, value)) / MAX) * r, i, count);

  const overlayByKey = {};
  (overlay || []).forEach((o) => { overlayByKey[o.key] = o.score; });

  const shape = shapeThrough(ordered.map((a) => a.score), pointFor);
  const overlayShape = overlay ? shapeThrough(ordered.map((a) => overlayByKey[a.key]), pointFor) : null;

  const unobserved = ordered.filter((a) => a.score == null);

  return (
    <div>
      {/* The viewBox is wider than the plot. Axis labels sit outside the outer
          ring and the ones at three and nine o'clock are anchored outward, so
          with a square viewBox they ran past the edge and were clipped —
          "Follow-through" rendered as "-through", which reads as a different
          parameter rather than as a truncation. */}
      <svg viewBox={`${-SIDE_ROOM} 0 ${size + SIDE_ROOM * 2} ${size}`} width="100%"
           data-cx={cx} data-cy={cy} data-r={r}
           style={{ maxWidth: size + SIDE_ROOM * 2, display: "block", margin: "0 auto" }} role="img"
           aria-label={`Scorecard radar across ${count} parameters, each scored out of ten.`}>
        {/* rings */}
        {RINGS.map((ring) => (
          <polygon
            key={ring}
            points={ordered.map((_, i) => polar(cx, cy, (ring / MAX) * r, i, count).join(",")).join(" ")}
            fill="none"
            stroke="var(--c-200)"
            strokeWidth={ring === MAX ? 1.2 : 0.7}
          />
        ))}

        {/* spokes, coloured by dimension so each arc is visually one thing */}
        {ordered.map((axis, i) => {
          const [x, y] = polar(cx, cy, r, i, count);
          return (
            <line key={axis.key} x1={cx} y1={cy} x2={x} y2={y}
                  stroke={DIMENSION_COLOR[axis.dimension] || "var(--c-200)"}
                  strokeWidth={0.8} opacity={0.45} />
          );
        })}

        {/* the team median, drawn first so the individual sits on top of it */}
        {overlayShape && overlayShape.edges.map((e, k) => (
          <line key={`m${k}`} x1={e.from[0]} y1={e.from[1]} x2={e.to[0]} y2={e.to[1]} stroke="var(--c-500)"
                strokeWidth={1.3} strokeDasharray="4 3" opacity={e.skips ? 0.35 : 0.75} />
        ))}

        {shape.fill && (
          <polygon points={shape.fill} fill="var(--blue-600)" fillOpacity={0.16} stroke="none" />
        )}
        {shape.edges.map((e, k) => (
          <line key={k} x1={e.from[0]} y1={e.from[1]} x2={e.to[0]} y2={e.to[1]} stroke="var(--blue-600)"
                strokeWidth={1.8} strokeLinecap="round" strokeDasharray={e.skips ? "3 4" : undefined}
                opacity={e.skips ? 0.55 : 1} />
        ))}

        {/* one marker per SCORED axis, at exactly its score; none for a blank */}
        {ordered.map((axis, i) => {
          if (axis.score == null) return null;
          const [x, y] = pointFor(axis.score, i);
          return (
            <circle key={axis.key} cx={x} cy={y} r={3.4} fill="var(--blue-600)"
                    data-key={axis.key} data-score={axis.score} />
          );
        })}

        {/* labels -- hover one for what the skill means */}
        {ordered.map((axis, i) => {
          const [x, y] = polar(cx, cy, r + 18, i, count);
          const anchor = Math.abs(x - cx) < 6 ? "middle" : x > cx ? "start" : "end";
          const lines = wrapLabel(axis.label);
          return (
            <text key={axis.key} x={x} y={y - (lines.length - 1) * 5} textAnchor={anchor}
                  dominantBaseline="middle" fontSize={9.5} fill="var(--c-600)" data-axis={axis.key}
                  style={{ cursor: axis.means ? "help" : undefined }}>
              {axis.means && <title>{`${axis.label}: ${axis.means}`}</title>}
              {lines.map((line, n) => (
                <tspan key={line + n} x={x} dy={n === 0 ? 0 : 10}>{line}</tspan>
              ))}
              <tspan x={x} dy={11} fontWeight={700}
                     fill={axis.score == null ? "var(--c-400)" : "var(--c-800)"}>
                {axis.score == null ? "—" : Number(axis.score).toFixed(1)}
              </tspan>
            </text>
          );
        })}
      </svg>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center",
                    marginTop: 4, fontSize: 10.5, color: "var(--c-500)" }}>
        {["tonality", "technicality"].map((dim) =>
          ordered.some((a) => a.dimension === dim || a.dimension === "both") ? (
            <span key={dim} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: DIMENSION_COLOR[dim] }} />
              {DIMENSION_LABEL[dim]}
            </span>
          ) : null,
        )}
        {overlay && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 14, height: 0, borderTop: "1.5px dashed var(--c-500)" }} />
            {overlayLabel}
          </span>
        )}
      </div>

      {unobserved.length > 0 && (
        // Named rather than left as a dent in the shape. "No objection was
        // raised" is a real, useful sentence; an unexplained collapse toward
        // the centre is read as a failing grade.
        <p style={{ fontSize: 11, color: "var(--c-500)", margin: "8px 0 0", textAlign: "center" }}>
          Not scored yet (the dashed edge skips {unobserved.length === 1 ? "it" : "them"}):{" "}
          {unobserved.map((a) => a.label).join(", ")}.
        </p>
      )}
    </div>
  );
}

// The shape through the scored axes only, in axis order. `edges` joins each
// scored axis to the next scored one round the dial; an edge that jumps over
// an unscored axis is marked `skips` and drawn dashed, so a gap in the
// evidence never looks like a dip in the score.
function shapeThrough(scores, pointFor) {
  const scored = scores.map((v, i) => (v == null ? null : { i, at: pointFor(v, i) })).filter(Boolean);
  if (!scored.length) return { fill: null, edges: [] };
  const edges = [];
  if (scored.length > 1) {
    scored.forEach((p, k) => {
      const next = scored[(k + 1) % scored.length];
      if (scored.length === 2 && k === 1) return;       // two points: one edge, not a doubled one
      const gap = (next.i - p.i + scores.length) % scores.length;
      edges.push({ from: p.at, to: next.at, skips: gap > 1 });
    });
  }
  const fill = scored.length >= 3 ? scored.map((p) => p.at.join(",")).join(" ") : null;
  return { fill, edges };
}

